// PG-24 — who HOLDS a movement pattern, and who held it before.
//
// Separate from `e1rmTrendMath.ts` because it answers a different question.
// That file ranks patterns and builds rows; this one owns a single rule — an
// exercise you have stopped training stops speaking for the movement — and the
// history that rule produces. Splitting it also keeps the ranking file off its
// 500-line ceiling, which it was 26 lines from when PG-24 landed.
import {
  DAY_MS,
  daysSince,
  meetsTrendThreshold,
  type E1rmPoint,
} from "@heraion/shared";

/**
 * The least a series has to be for this module to reason about it: a name for
 * the cell it competes for, and its points in order.
 */
export type OccupancySeries = {
  slug: string;
  points: readonly E1rmPoint[];
};

/**
 * Days since an exercise's last e1RM reading after which it stops OCCUPYING
 * its movement pattern and becomes that pattern's PREVIOUS occupant.
 *
 * ⛔ Deduced from his archive, never declared — re-measured on 2026-09-17 and
 * it must be re-measured before it is ever changed
 * ([[feedback_derive_thresholds_from_data_not_constants]]).
 *
 * Days since the last reading, across the 20 tracks that clear
 * `meetsTrendThreshold` in the 84-day window:
 *   0,1 · 0,1 · 0,1 · 2,1 · 2,1 · 3,1 · 3,1 · 3,1 · 6,1 · 6,1 · 11,0 · 11,0 ·
 *   16,1 · 17,1 · 23,1 · 30,1 · 31,8 · 35,1 · 41,9 · 47,9
 * The one wide gap is 23,1 → 30,1 (7 days of nothing); measuring the same way
 * a day earlier gave 22,6 → 29,6. The knee is the middle of the band the two
 * measurements share, ≈26.
 *
 * ⚠️ And the band is what matters, not the number: every value from 21 to 27
 * elects exactly the same occupants on his data. A future measurement that
 * moves this by a day or two changes nothing anyone can see — one that
 * collapses the gap means the shape of his training changed, and THAT is the
 * signal to look at, not the constant.
 *
 * ⛔ It does NOT live in `trendAdmissibility.ts` next to the other two. Those
 * decide whether a series is a trend at all; this one decides which admissible
 * series holds the cell today. Filing it with them would invite filtering on
 * it, and dropping the stale series is exactly what PG-24 exists to prevent:
 * the previous occupant has to survive in order to be drawn.
 */
export const OCCUPANT_FRESHNESS_DAYS = 26;

/**
 * The election AS IT STOOD at `atMs`: admissible on the points seen by then,
 * still fresh at that moment, heaviest last reading wins.
 *
 * ⛔ The weight rule is untouched — PG-24 slice A adds recency, it does not
 * change what "best" means. `latest` is the most recent reading, exactly as
 * the panel has always ranked, so the only new reason to lose is having
 * stopped training the movement.
 */
const occupantAt = <T extends OccupancySeries>(
  candidates: readonly T[],
  atMs: number,
): T | undefined => {
  let best: T | undefined;
  let bestKg = -Infinity;
  for (const c of candidates) {
    // Points are chronological (the funnel sorts once), so a prefix is
    // exactly "what had been logged by then".
    let seen = c.points.length;
    while (seen > 0 && (c.points[seen - 1]?.atMs ?? 0) > atMs) seen -= 1;
    if (seen === 0) continue;
    const upToNow = c.points.slice(0, seen);
    if (!meetsTrendThreshold(upToNow)) continue;
    const last = upToNow[upToNow.length - 1];
    if (last === undefined) continue;
    const age = daysSince(last.atMs, atMs);
    if (age === null || age > OCCUPANT_FRESHNESS_DAYS) continue;
    if (last.kgPerRep1 > bestKg) {
      best = c;
      bestKg = last.kgPerRep1;
    }
  }
  return best;
};

/**
 * Walk the election forward and keep the LAST change of hands.
 *
 * ⛔ Not derivable from today's standings alone, which is the shortcut worth
 * naming: "heaviest among the ones that have gone stale" is a different
 * question and gives a different answer. On his hinge cell it returns
 * `dumbbell-romanian-deadlift`, which never held the cell; the walk returns
 * `romanian-deadlift`, which held it until the deadlift took over.
 *
 * The instants that matter are the ones where the answer CAN change: a
 * candidate gaining a point, and a candidate going stale — the second is why
 * a cell can change hands on a day nobody trained.
 */
export const deriveOccupancy = <T extends OccupancySeries>(
  candidates: readonly T[],
  nowMs: number,
): { current: T; previous: T | undefined } | undefined => {
  const instants = new Set<number>([nowMs]);
  for (const c of candidates) {
    for (const pt of c.points) instants.add(pt.atMs);
    const last = c.points[c.points.length - 1];
    if (last !== undefined) {
      instants.add(last.atMs + (OCCUPANT_FRESHNESS_DAYS + 1) * DAY_MS);
    }
  }
  // ⛔ Who holds the cell is asked of TODAY first, and the history only names
  // the one before. Reading the last winner off the walk instead looks
  // equivalent and is not: a pattern trained hard and then dropped would keep
  // its final holder on screen forever, which is the exact bug PG-24 exists to
  // kill — with the freshness rule in place and still showing a stale winner.
  const current = occupantAt(candidates, nowMs);
  if (current === undefined) return undefined;

  let held: T | undefined;
  let previous: T | undefined;
  for (const at of [...instants].filter((t) => t <= nowMs).sort((a, b) => a - b)) {
    const winner = occupantAt(candidates, at);
    // An empty spell is not a handover: the cell simply had nobody. Whoever
    // held it before still counts as the predecessor when someone returns.
    if (winner === undefined) continue;
    if (held !== undefined && winner.slug !== held.slug) previous = held;
    held = winner;
  }
  return { current, previous };
};
